/**
 * Contexto de la búsqueda (finalidad, n° de caso, rol de la persona).
 *
 * Es lo ÚNICO que la SPA guarda en el navegador, y en sessionStorage: vive solo en
 * la pestaña (un analista puede llevar dos casos en dos pestañas) y desaparece al
 * cerrarla. Los resultados, el historial y los datos personales quedan solo en la
 * memoria de React Query.
 */

import { PURPOSES, SUBJECT_ROLES, type SearchContext } from "../../shared/contracts.js";

const KEY = "sentinel.searchContext.v1";

export type Purpose = (typeof PURPOSES)[number];
export type SubjectRole = (typeof SUBJECT_ROLES)[number];

/** Estado del formulario: la finalidad puede estar vacía hasta que el usuario elija. */
export interface ContextDraft {
  purpose: Purpose | "";
  purposeDetail: string;
  caseRef: string;
  subjectRole: SubjectRole | "";
}

export const EMPTY_CONTEXT: ContextDraft = { purpose: "", purposeDetail: "", caseRef: "", subjectRole: "" };

/** Almacenamiento mínimo (inyectable en tests, que no tienen window). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function defaultStore(): KeyValueStore | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    // Navegadores con almacenamiento bloqueado: se trabaja solo en memoria.
    return null;
  }
}

const isPurpose = (v: unknown): v is Purpose => typeof v === "string" && (PURPOSES as readonly string[]).includes(v);
const isRole = (v: unknown): v is SubjectRole => typeof v === "string" && (SUBJECT_ROLES as readonly string[]).includes(v);
const str = (v: unknown, max: number): string => (typeof v === "string" ? v.slice(0, max) : "");

export function loadContext(store: KeyValueStore | null = defaultStore()): ContextDraft {
  if (!store) return { ...EMPTY_CONTEXT };
  try {
    const raw = store.getItem(KEY);
    if (!raw) return { ...EMPTY_CONTEXT };
    const o = JSON.parse(raw) as Record<string, unknown>;
    return {
      purpose: isPurpose(o.purpose) ? o.purpose : "",
      purposeDetail: str(o.purposeDetail, 500),
      caseRef: str(o.caseRef, 80),
      subjectRole: isRole(o.subjectRole) ? o.subjectRole : "",
    };
  } catch {
    return { ...EMPTY_CONTEXT };
  }
}

export function saveContext(ctx: ContextDraft, store: KeyValueStore | null = defaultStore()): void {
  try {
    store?.setItem(KEY, JSON.stringify(ctx));
  } catch {
    // Cuota llena o almacenamiento bloqueado: el contexto sigue en memoria.
  }
}

export function clearContext(store: KeyValueStore | null = defaultStore()): void {
  try {
    store?.removeItem(KEY);
  } catch {
    // Igual que arriba: nada que limpiar si no hay almacenamiento.
  }
}

export type ContextErrors = Partial<Record<"purpose" | "purposeDetail" | "caseRef", string>>;

/**
 * Valida el borrador con las mismas reglas del contrato (finalidad obligatoria;
 * si es "otra", el detalle ≥ 3 caracteres) y lo convierte al cuerpo de la API.
 */
export function toSearchContext(d: ContextDraft): { ok: true; value: SearchContext } | { ok: false; errors: ContextErrors } {
  const errors: ContextErrors = {};
  if (!d.purpose) errors.purpose = "Indica la finalidad de la búsqueda (exigida por la Ley 21.719).";
  const detail = d.purposeDetail.trim();
  if (d.purpose === "other" && detail.length < 3) errors.purposeDetail = "Especifica la finalidad (mínimo 3 caracteres).";
  if (detail.length > 500) errors.purposeDetail = "Máximo 500 caracteres.";
  const caseRef = d.caseRef.trim();
  if (caseRef.length > 80) errors.caseRef = "Máximo 80 caracteres.";
  if (!d.purpose || Object.keys(errors).length > 0) return { ok: false, errors };
  return {
    ok: true,
    value: {
      purpose: d.purpose,
      ...(d.purpose === "other" && detail ? { purposeDetail: detail } : {}),
      ...(caseRef ? { caseRef } : {}),
      ...(d.subjectRole ? { subjectRole: d.subjectRole } : {}),
    },
  };
}
